import { useEffect, useMemo, useRef, useState } from 'react';
import Modal from './Modal';
import { documentsApi, commentsApi, suppliersApi, formatCurrency } from '../utils/api';
import { toast } from './Toast';
import { formatDateShort, isDateInRange, ymdKey } from '../utils/dateFormat';
import { previousMonthRange, PRESETS_MONTHLY } from '../utils/period';
import { reconcileNet, signedNet, linkTotals, linkGap } from '../utils/reconcile';
// confirmed_at is SQLite UTC — show the ISRAEL calendar day, not a sliced UTC one.
import { formatUtcDay as formatUtcDate } from '../utils/dateFormat';
import { track, EVENTS } from '../utils/analytics';
import Icon from './Icon';
import EmptyState from './EmptyState';
import PeriodPicker from './PeriodPicker';
import ConfirmModal from './ConfirmModal';
import CommentsModal from './CommentsModal';
import ReviewDocPane from './ReviewDocPane';
import { askableMissing, missingNotesComment, missingListAlreadySent, missingKindLabel, missingAmountText, missingTotalText } from '../utils/missingNotesComment';
import { useIsMobile } from '../utils/useIsMobile';
import { useSortable } from '../utils/useSortable';
import MobileSort from './MobileSort';
import { LINK_SORT_ACCESSORS, LINK_SORT_DEFAULT, LINK_SORT_OPTIONS, matchesNoteSearch, missingRow, creditRow, blockerLabel, dateSpan, offInvoiceLast, oldestFirst } from '../utils/linkRows';
import { parseTenantPath, withTenantUrlRid } from '../utils/tenantPath';

// A note opened in its own tab, so the ticks here are not lost. The tab's own
// URL is its tenancy; a plain <a> needs the full path, the facade's prefix
// applies to in-app navigation only.
const noteHref = (id) => withTenantUrlRid(parseTenantPath(window.location.pathname).urlRid, `/document/${id}`);
const NOTE_LINK_TITLE = 'פתיחת התעודה בלשונית חדשה';
const countNotes = (n) => (n === 1 ? 'תעודה אחת' : `${n} תעודות`);

// Post-save modal: presented after a חשבונית מרכזת is saved (or on demand).
// Lists the supplier's unlinked תעודות משלוח plus any already-linked-here, and
// lets the admin pick which ones the consolidated invoice covers.
//
// EVERY figure here is נטו. The notes carry no VAT of their own — the VAT
// lives on the מרכזת — so the reconciliation runs on subtotals
// (utils/reconcile.js), and the header anchors on the invoice's נטו for the
// same reason: it is the number the running sum is supposed to reach. The
// gross total rides along as context only. An earlier revision led with the
// gross figure, which put כולל מע״מ in the header against נטו everywhere
// else — the two can differ by 18% and the mismatch banner then read as a
// bug ("the modal says the totals differ but I can see they match").
//
// Callers that cannot provide a subtotal fall back to the gross-only header;
// reconciliation still runs (against 0) so the save-anyway path stays open.
//
// THE SCAN. The whole job here is comparing the invoice's printed list with the
// candidates, so above 1024px (the document page's own pane breakpoint) the
// dialog grows into a split with the invoice's scan beside the list; below it
// there is no room, and a button opens the scan over the list.
//
// THE PERIOD FILTER. A monthly מרכזת covers 30-60 notes and the server returns
// every unlinked note of that supplier ever, so the list opens narrowed to the
// previous calendar month — the month a מרכזת issued in the first days of the
// next one almost always covers — with those notes already selected. The
// filter is a VIEW, never a selection: selections survive every range change,
// and the running sum, the banner and the id set that gets saved always cover
// every selected row, visible or not. They have to: doSave sends the full
// replacement set, so a row silently dropped from `selected` is an unlink.

// A decision button and what it does, instantly on hover (.btn-tip) and for a
// screen reader through the hidden text. A phone has no hover, so no tip there.
function DecisionButton({ tip, tipId, className, ...props }) {
  return (
    <>
      <button className={`${className} btn-tip`} data-tip={tip} aria-describedby={tipId} {...props} />
      <span id={tipId} hidden>{tip}</span>
    </>
  );
}

// Same notes, different numbering (see canConfirmByAmount in the component).
const canConfirmByAmountOnApproval = (askableCount, matches, invoiceNet, selectedCount, confirmedNow) =>
  askableCount > 0 && matches && invoiceNet > 0 && selectedCount > 0 && !confirmedNow;

export default function LinkDeliveryNotesModal({
  invoiceId,
  invoiceTotal,
  invoiceSubtotal,
  // Titles the comment thread opened from the missing-notes panel; the same
  // "מסמך …" label SupplierDocumentsPage gives it, so it reads as one thread.
  invoiceDocNum,
  supplierName,
  // 'טיוטה' | 'תקין' | 'לא תקין' — gates the mark-as-invalid offer below.
  invoiceStatus = '',
  // How many links this invoice ALREADY holds. Auto-selecting a month is a
  // first-link convenience only: on a re-edit the admin's existing set is the
  // answer, and quietly re-adding a note they deliberately excluded would be
  // written straight through by the full-replace save.
  existingLinkCount = 0,
  // { from, to } (YYYY-MM-DD) to open on instead of the previous month — set
  // when a plain invoice was retyped from the "open delivery notes" chip, so
  // the modal shows (and pre-ticks) exactly the notes that chip counted.
  initialRange = null,
  // { supplierId, dateFrom, dateTo, monthLabel } — offer "אשר גם לתשלום" for
  // the invoice's supplier-month, or null for no offer. The caller decides
  // (payment tracking on, manager+, month not yet approved). The approval
  // flow moves to the next draft the moment this modal closes, so this is the
  // last place the month can be approved without coming back. Ora, 2026-10-01.
  monthApproval = null,
  // The invoice's pages, in ReviewDocPane's shape, and its rotate handler.
  // Without files the dialog stays the single narrow column.
  files = null,
  onRotate,
  // A draft's approve button opens this window BEFORE anything is saved, so the
  // document stays טיוטה until a button here decides (owner, 03/11/2026, #381).
  // { save(status) → the status written, or falsy when refused, keep() }. The page
  // owns the save, because the edits and the approval's side effects are its.
  decision = null,
  onClose,
  onSaved,
}) {
  const [loading, setLoading]     = useState(true);
  const [saving,  setSaving]      = useState(false);
  const [rows,    setRows]        = useState([]);
  const [selected, setSelected]   = useState(new Set());
  // { covered_count, matched, missing, extra, missing_total } — null whenever
  // the invoice listed no delivery-note references at all. "Nothing missing"
  // and "we never read a list" must not render the same, so the whole panel
  // hangs off this being non-null rather than off missing.length.
  const [coverage, setCoverage]   = useState(null);
  // Plain local state — deliberately NOT utils/sharedDateRange.js. That store
  // is the page-level period; writing to it from here would retune /documents
  // and /suppliers behind the user's back while they link notes.
  const [range, setRange] = useState(() => initialRange || previousMonthRange());
  // The fetch resolves after mount, and the preselect below needs the range as
  // it stands then — reading state inside the promise would close over the
  // initial render's value. A ref is the honest way to say "current".
  const rangeRef = useRef(range);
  rangeRef.current = range;
  const didPreselectRef = useRef(false);
  // The ticks as the window opened, so Escape can tell whether work would be lost.
  const openedSelectionRef = useRef(null);
  // The mismatch confirm, rendered directly rather than through useConfirm:
  // that hook snapshots its options into state, which freezes a checkbox
  // inside them at its initial `checked` and closes onConfirm over a stale
  // value. Same shape as CreditsOpenPage, for the same reason.
  const [confirmState, setConfirmState] = useState(null);
  const [markInvalid,  setMarkInvalid]  = useState(true);
  const [approveMonth, setApproveMonth] = useState(false);
  // null = untouched, so the box follows listSent, which arrives after the open.
  const [decisionComment, setDecisionComment] = useState(null);
  // Which decision button is saving, so it alone says so.
  const [pressed, setPressed] = useState(null);
  // What the decision saved, kept for a link save retried from the approved footer.
  const decidedRef = useRef({ invalidMarker: false, noMonth: false });
  // The missing-notes report handed to the invoice's comment thread, so the
  // customer is told (and emailed) which notes the supplier billed for.
  // false | 'panel' (the button; this modal stays open after) | 'afterSave'
  // (opened by a save; closing it closes this modal too, so the upload flow
  // moves on only once the comment has been dealt with).
  const [commentOpen,  setCommentOpen]  = useState(false);
  const [sendComment,  setSendComment]  = useState(true);
  // Pre-tick "send" only the first time: a re-edit weeks later (one more note
  // arrived) must not push the customer a duplicate, outdated request.
  const [listSent,     setListSent]     = useState(false);
  // The invoice's status as the server holds it NOW. The prop is the caller's
  // copy, and the modal usually opens right after a save that just moved it —
  // every approve path once passed the pre-save טיוטה, which hid the send
  // button on an invoice the customer could already see.
  const [liveStatus,   setLiveStatus]   = useState(null);
  const [scanOpen,     setScanOpen]     = useState(false);
  // A view, like the range: it hides rows, never unticks them.
  const [search,       setSearch]       = useState('');
  // Either fetch failing means the ticks on screen are not what was saved, so no
  // save of any kind is offered until a retry reads them: a guess saved as real
  // links would also spend the kept ticks, with no confirm if the sums happen to
  // close. Listing the notes stays possible, for reading.
  const [listFailed,     setListFailed]     = useState(false);
  const [coverageFailed, setCoverageFailed] = useState(false);
  const [loadAttempt,    setLoadAttempt]    = useState(0);
  // Nothing on the invoice named its notes, so the month's notes were ticked as a
  // guess. Said on screen: a gap from a wrong guess reads like a gap in the money.
  const [ticksAreGuess,  setTicksAreGuess]  = useState(false);
  const [guessLeftOut,   setGuessLeftOut]   = useState(0);
  const [openCredits,    setOpenCredits]    = useState([]);
  const scanOpenBtnRef = useRef(null);
  const status = liveStatus || invoiceStatus;
  const closeComment = () => {
    if (commentOpen === 'afterSave') onClose();
    else setCommentOpen(false);
  };
  // 640 — the table-density breakpoint, not the 720 filter one: what changes
  // here is a five-column table that stops fitting, same trigger as every
  // other table→list swap in the app.
  const isMobile = useIsMobile(640);
  const isNarrow = useIsMobile(1024);
  const hasScan = Array.isArray(files) && files.length > 0;
  const split = hasScan && !isNarrow;

  useEffect(() => {
    setLoading(true);
    setListFailed(false);
    setCoverageFailed(false);
    // A retry starts over: the ticks from a failed read were only a guess, and if
    // this read fails too, that guess must not be left on screen to be saved.
    didPreselectRef.current = false;
    openedSelectionRef.current = null;
    setRows([]);
    setSelected(new Set());
    setCoverage(null);
    setTicksAreGuess(false);
    setGuessLeftOut(0);
    setOpenCredits([]);
    Promise.all([
      documentsApi.unlinkedDeliveryNotes(invoiceId),
      // A failure here leaves the notes readable but blocks saving: this read
      // is also what carries the kept ticks.
      documentsApi.coverage(invoiceId).catch(() => { setCoverageFailed(true); return null; }),
      // Only to know whether the missing list was already sent; optional too.
      Promise.resolve().then(() => commentsApi.getAll('document', invoiceId)).catch(() => null),
    ])
      .then(([r, cov, comments]) => {
        const list = Array.isArray(r.data) ? r.data : [];
        setRows(list);
        if (cov?.data?.status) setLiveStatus(cov.data.status);
        setListSent(missingListAlreadySent(comments?.data));
        setOpenCredits(cov?.data?.open_credit_notes ?? []);
        const c = cov?.data?.covered_count > 0 ? cov.data : null;
        setCoverage(c);
        // The invoice named its own notes, so the month heuristic the filter
        // encodes is no longer a guess worth making: open the range wide. A
        // מרכזת routinely reaches across the month boundary, and a matched
        // note left counted as "מחוץ לטווח" is invisible on the very screen
        // that just proved it belongs here. Done before the branches below so
        // it applies on a re-edit too, where the selection comes from the
        // existing links rather than from the match.
        if (c) {
          didPreselectRef.current = true;
          setRange({ from: '', to: '' });
        }
        // Ticks kept on the draft win over everything below: they are the
        // admin's latest word, including "none of these" and including a change
        // to links saved earlier. Intersected with the list, so a note since
        // linked elsewhere or deleted drops out. Opened wide for the same reason
        // as above: a ticked note must not hide outside the default month.
        // Every kept tick gone stale falls through to the choices below: an empty
        // selection there would be an accident, not the admin's word.
        const pending = cov?.data?.pending_note_ids;
        const kept = new Set(Array.isArray(pending) ? pending : []);
        if (Array.isArray(pending) && (pending.length === 0 || list.some(n => kept.has(n.id)))) {
          didPreselectRef.current = true;
          setRange({ from: '', to: '' });
          setSelected(new Set(list.filter(n => kept.has(n.id)).map(n => n.id)));
          return;
        }
        // Pre-select rows already linked to this invoice (so the user can
        // re-edit without losing existing selections).
        const linkedHere = list.filter(n => n.already_linked_here);
        if (linkedHere.length > 0 || existingLinkCount > 0) {
          setSelected(new Set(linkedHere.map(n => n.id)));
          return;
        }
        // First link against a listed invoice: tick exactly what matched.
        if (c) {
          setSelected(new Set(c.matched.map(m => m.document_id)));
          return;
        }
        // First link: tick everything in the opening month. One-shot — if this
        // re-ran on a range change it would wipe the user's manual work every
        // time they stepped a month, which is the opposite of what the filter
        // is for. Undated notes are never auto-selected: they are visible in
        // every range precisely because we can't place them in a month, and
        // that is not a reason to include them in the money.
        if (didPreselectRef.current) return;
        didPreselectRef.current = true;
        // Nor is a note addressed to another business: it is only here because the
        // supplier matches. It stays in the list, marked, in case the read was wrong.
        const { from, to } = rangeRef.current;
        const inMonth = list.filter(n => ymdKey(n.date) && isDateInRange(n.date, from, to));
        const ticked = inMonth.filter(n => n.recipient_match !== 'mismatch');
        setSelected(new Set(ticked.map(n => n.id)));
        setTicksAreGuess(cov != null && ticked.length > 0);
        setGuessLeftOut(inMonth.length - ticked.length);
      })
      .catch(() => { setListFailed(true); toast('שגיאה בטעינת תעודות משלוח', 'error'); })
      .finally(() => setLoading(false));
  }, [invoiceId, existingLinkCount, loadAttempt]);

  // Re-read the list and the status, keeping the ticks. For coming back from a
  // note opened in its own tab (its amount may have changed, it may be gone) and
  // for a save refused because the status moved in another tab, which would
  // otherwise leave a window with no working save button.
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const refreshList = async () => {
    try {
      const [r, cov] = await Promise.all([
        documentsApi.unlinkedDeliveryNotes(invoiceId),
        documentsApi.coverage(invoiceId).catch(() => null),
      ]);
      const list = Array.isArray(r.data) ? r.data : [];
      setRows(list);
      if (cov?.data) {
        if (cov.data.status) setLiveStatus(cov.data.status);
        setCoverage(cov.data.covered_count > 0 ? cov.data : null);
        setOpenCredits(cov.data.open_credit_notes ?? []);
      }
      const ids = new Set(list.map(n => n.id));
      const gone = [...selectedRef.current].filter(id => !ids.has(id));
      if (gone.length > 0) {
        setSelected(new Set([...selectedRef.current].filter(id => ids.has(id))));
        toast(gone.length === 1 ? 'תעודה שסומנה כבר לא זמינה לקישור' : `${gone.length} תעודות שסומנו כבר לא זמינות לקישור`, 'error');
      }
    } catch (e) {
      console.warn('[link-modal] refresh failed', e.message);
    }
  };
  const busyRef = useRef(true);
  busyRef.current = loading || saving;
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible' && !busyRef.current) refreshList(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId]);

  // Runs when loading ends: the fetch sets the ticks before it clears `loading`.
  useEffect(() => {
    if (!loading && !openedSelectionRef.current) openedSelectionRef.current = new Set(selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const rangeRows = useMemo(
    () => rows.filter(r => isDateInRange(r.date, range.from, range.to)),
    [rows, range]
  );
  const visibleRows = useMemo(
    () => rangeRows.filter(r => matchesNoteSearch(r.doc_num, search)),
    [rangeRows, search]
  );

  // Select-all acts on what is on screen and nothing else: checking unions the
  // visible ids in, unchecking subtracts only those. A note selected in
  // another month is none of this control's business.
  const visibleSelectedCount = useMemo(
    () => visibleRows.reduce((n, r) => n + (selected.has(r.id) ? 1 : 0), 0),
    [visibleRows, selected]
  );
  const allVisibleSelected  = visibleRows.length > 0 && visibleSelectedCount === visibleRows.length;
  const someVisibleSelected = visibleSelectedCount > 0 && !allVisibleSelected;
  // Out of the RANGE only: a tick hidden by a search is not "outside" anything.
  const outsideRangeCount   = selected.size - rangeRows.reduce((n, r) => n + (selected.has(r.id) ? 1 : 0), 0);

  const toggleAllVisible = () => {
    setSelected(prev => {
      const next = new Set(prev);
      if (allVisibleSelected) for (const r of visibleRows) next.delete(r.id);
      else                    for (const r of visibleRows) next.add(r.id);
      return next;
    });
  };

  // Derived from ALL rows, never visibleRows — this is what keeps the sum, the
  // banner and the saved payload independent of the filter.
  const selectedRows = useMemo(
    () => rows.filter(r => selected.has(r.id)),
    [rows, selected]
  );
  const { linkedNet, invoiceNet, diff, matches } = reconcileNet(selectedRows, invoiceSubtotal);
  const invTotal = Math.round((parseFloat(invoiceTotal) || 0) * 100) / 100;
  const isDraft = status === 'טיוטה';

  // Notes the invoice bills for that aren't in the system. A finding in its
  // own right: unlike a money gap it names exactly what is wrong, and it can
  // be true on an invoice whose net we can't reconcile at all.
  const missingCount = coverage?.missing.length ?? 0;
  // The list the customer can be asked about: not a reference we already hold.
  const askableCount = coverage ? askableMissing(coverage).missing.length : 0;

  // Notes the invoice names that ARE in the system but still drafts. A draft
  // is never linkable (the server refuses it), and it is not missing either —
  // reporting it as missing could send the customer a list asking for a note
  // we already hold. Approving it in /drafts is the way forward. Said in the
  // coverage panel and again in the mismatch confirm: a gap that the drafts
  // explain is not the finding the confirm otherwise suggests.
  const awaitingCount = coverage?.awaiting_approval?.length ?? 0;
  const awaitingText = awaitingCount === 1
    ? 'תעודה אחת מהחשבונית ממתינה לאישור בטיוטות'
    : `${awaitingCount} תעודות מהחשבונית ממתינות לאישור בטיוטות`;

  // "Same notes, different numbering". Some suppliers print one numbering
  // series on the מרכזת and another on the notes (ירוקלה), so every reference
  // reads as missing even when the right notes are linked and the sums close.
  // The admin may confirm the invoice by amount — offered only while the
  // CURRENT selection reconciles, and the server re-checks the same rule
  // (lib/coverageConfirm.js). An existing confirmation counts only while the
  // selection still reconciles: unticking a note on screen takes it back, the
  // same way the server drops it on save.
  const confirmedNow = !!coverage?.confirmed && matches && missingCount > 0;
  // Not on a draft: the confirmation stands on real links, and a draft has none
  // (owner, 02/11/2026). The window on the approve button offers it as its own
  // decision, and ערוך קישורים after approval as this button (#381).
  // And not when the server already knows what every reference is: "another
  // numbering" is then not the explanation.
  const canConfirmByAmount = askableCount > 0 && matches && invoiceNet > 0
    && selected.size > 0 && !confirmedNow && status !== 'טיוטה';

  // Only offer the flag where it can actually do something:
  //   • no finding at all — nothing to flag. invoiceNet <= 0 alone is not a
  //     finding: with a net of 0 the ₪10 tolerance floor makes EVERY selection
  //     a mismatch, so a pre-checked box would flag correct invoices (the trap
  //     that once made this modal show a phantom הפרש on every invoice). A
  //     missing note is a real finding regardless of the net, so it counts.
  //   • already לא תקין — the flag is a no-op, and offering it would be a
  //     control that lies.
  //
  // A draft IS offered the flag. It leaves the /drafts queue when flagged,
  // which was the original reason to refuse — but approve-all deliberately
  // skips מרכזת, so a draft מרכזת never leaves that queue on its own anyway,
  // and "needs checking" is the truer state than "not reviewed yet". Ora's
  // call, 2026-09-05: a finding on the first save must be recordable.
  const hasFinding = !matches || (missingCount > 0 && !confirmedNow);

  // Credit notes for the admin to check. One the invoice itself names is already its
  // red row, which says so, and its money is already in the missing total. When the
  // window is open on all dates, an invoice that names its notes limits them to its
  // own dates: otherwise years of old credit notes would read as explaining this gap.
  const creditPool = useMemo(() => {
    const named = new Set(confirmedNow ? [] : (coverage?.missing ?? []).map(m => m.blocker?.document_id));
    return openCredits.filter(c => !named.has(c.id));
  }, [openCredits, coverage, confirmedNow]);
  const wideOpen = !range.from && !range.to;
  const invoiceSpan = useMemo(() => (wideOpen && coverage ? dateSpan([
    ...(confirmedNow ? [] : coverage.missing.map(m => m.ref_date)),
    ...rows.filter(r => r.covered_ref).map(r => r.date),
  ]) : null), [wideOpen, coverage, confirmedNow, rows]);
  const creditsInRange = useMemo(() => {
    const bounds = invoiceSpan ?? range;
    return creditPool.map(creditRow).filter(r => isDateInRange(r.date, bounds.from, bounds.to));
  }, [creditPool, invoiceSpan, range]);
  const creditsWhere = !wideOpen ? 'בטווח' : invoiceSpan ? 'בתקופת החשבונית' : 'אצל הספק';

  // The list, in the order the admin reads the invoice: candidates and missing
  // references together. Once confirmed by amount those references are the same
  // notes under another numbering, so they are not rows of their own.
  const listCandidates = useMemo(() => {
    const missingRows = confirmedNow ? [] : (coverage?.missing ?? [])
      .map(missingRow)
      .filter(r => isDateInRange(r.date, range.from, range.to) && matchesNoteSearch(r.doc_num, search));
    const creditRows = creditsInRange.filter(r => matchesNoteSearch(r.doc_num, search));
    return oldestFirst([...visibleRows, ...missingRows, ...creditRows]);
  }, [visibleRows, coverage, confirmedNow, creditsInRange, range, search]);
  const { sorted: sortedList, sort: listSort, setSortTo: setListSort, indicator: sortIcon, headerProps: sortableTh } =
    useSortable(listCandidates, LINK_SORT_ACCESSORS, LINK_SORT_DEFAULT);
  const isOffInvoice = (r) => !r.isMissing && !r.isCredit && !!coverage && !confirmedNow && !r.covered_ref;
  const creditsNet = Math.round(creditsInRange.reduce((s, r) => s + r.net, 0) * 100) / 100;
  // Credit notes last of all: they are in no sum, and among the named notes they
  // would break the line-by-line reading.
  const listRows = offInvoiceLast(offInvoiceLast(sortedList, isOffInvoice), r => r.isCredit);
  const totals = linkTotals(linkedNet, coverage, confirmedNow);
  const gap = linkGap(totals, invoiceNet);
  const gapLabel = (g) => {
    if (g.kind === 'short') return `חסר מול המרכזת: ${formatCurrency(g.amount)}`;
    if (g.kind === 'over') return `עודף מול המרכזת: ${formatCurrency(g.amount)}`;
    if (g.kind === 'explained') return 'החסרות מסבירות את ההפרש';
    return g.amount < 0.01 ? 'תואם למרכזת' : `תואם למרכזת · הפרשי מחיר/עיגול: ${formatCurrency(g.amount)}`;
  };
  const canMarkInvalid = (invoiceNet > 0 || missingCount > 0) && status !== 'לא תקין';
  // Send the missing list to the customer as part of the save — only once the
  // customer can see the document: a draft qualifies only if this save flags it.
  const commentVisible = status !== 'טיוטה' || (canMarkInvalid && markInvalid);

  // The draft's flag button already said what it does, so its confirm asks about
  // the consequences rather than offering the same choice again as a checkbox.
  const confirmDraftFlag = () => {
    setMarkInvalid(true);
    setSendComment(!listSent);
    const missingLine = missingCount === 0 ? null
      : coverage.missing_without_amount === missingCount
        ? `לא נמצאו לקישור: ${countNotes(missingCount)} · החשבונית אינה מפרטת סכום לכל תעודה`
        : `לא נמצאו לקישור: ${countNotes(missingCount)} · ${formatCurrency(coverage.missing_total)}`;
    const gapLine = gap?.kind === 'short' || gap?.kind === 'over' ? gapLabel(gap) : null;
    setConfirmState({
      title: selected.size > 0 ? `לסמן כלא תקין ולקשר ${countNotes(selected.size)}?` : 'לסמן כלא תקין בלי לקשר תעודות?',
      body: [
        'המסמך יעבור מטיוטה ל"לא תקין" וייצא מתור הטיוטות.',
        selected.size === 1 ? 'התעודה שסומנה תיקשר בפועל למרכזת.'
          : selected.size > 1 ? `${selected.size} התעודות שסומנו ייקשרו בפועל למרכזת.` : null,
        awaitingCount > 0 ? `${awaitingText} — אפשר לאשר ${awaitingCount === 1 ? 'אותה' : 'אותן'} ולחזור לקשר.` : null,
      ],
      detail: (missingLine || gapLine) && <>{missingLine}{missingLine && gapLine && <br />}{gapLine}</>,
      confirmLabel: selected.size > 0 ? 'סמן כלא תקין וקשר' : 'סמן כלא תקין',
      tone: 'primary',
    });
  };

  const handleSave = () => {
    if (isDraft) {
      confirmDraftFlag();
      return;
    }
    if (hasFinding) {
      const fmtDiff = (diff > 0 ? '+' : '') + formatCurrency(diff);
      // Clearing every link is a normal intermediate step ("let me redo
      // this"), not a finding — so don't arm the flag for it. A missing note
      // survives that reasoning: it is the invoice's own claim, not something
      // the selection can explain away.
      setMarkInvalid(canMarkInvalid && (selected.size > 0 || missingCount > 0));
      setSendComment(!listSent);
      const moneyDetail = `תעודות משלוח נטו: ${formatCurrency(linkedNet)} · מרכזת נטו: ${formatCurrency(invoiceNet)} · הפרש: ${fmtDiff}`;
      const awaitingBody = awaitingCount > 0
        ? [`${awaitingText} — אפשר לאשר ${awaitingCount === 1 ? 'אותה' : 'אותן'} ולחזור לקשר.`]
        : [];
      setConfirmState(
        missingCount > 0
          ? {
              title: missingCount === 1 ? 'תעודה אחת לא נמצאה לקישור' : `${missingCount} תעודות לא נמצאו לקישור`,
              message: 'החשבונית מחייבת על תעודות שלא נמצאו. לשמור את הקישור בכל זאת?',
              body: awaitingBody,
              // Same rule as the panel above: when nothing missing carries a
              // printed amount, "סה״כ חסר: ₪0.00" reads as "nothing of value
              // is missing" — the opposite of what the dialog is asking about.
              detail: (coverage.missing_without_amount === missingCount
                ? 'החשבונית אינה מפרטת סכום לכל תעודה'
                : `סה״כ חסר: ${formatCurrency(coverage.missing_total)}`)
                + (matches ? '' : ` · ${moneyDetail}`),
              confirmLabel: 'שמור בכל זאת',
            }
          : {
              title: 'הסכומים אינם תואמים',
              message: 'לשמור את הקישור בכל זאת?',
              body: awaitingBody,
              // The three figures were crammed into one native-dialog string.
              // In a real dialog the discrepancy is the detail block, which is
              // what the user is actually being asked to accept.
              detail: moneyDetail,
              confirmLabel: 'שמור בכל זאת',
            }
      );
      return;
    }
    doSave(false);
  };

  // `comment`: the decision footer's own box, read at the click. The status it is
  // about only exists after this save, so commentVisible cannot speak for it.
  const doSave = async (flag, confirmByAmount = false, { comment } = {}) => {
    const { invalidMarker, noMonth } = decidedRef.current;
    // Close the dialog first: useConfirm used to guarantee this, and a slow
    // handler must not leave a dead dialog on screen.
    setConfirmState(null);
    setSaving(true);
    try {
      const ids = [...selected];
      const r = confirmByAmount
        ? await documentsApi.saveLinks(invoiceId, ids, false, true)
        : invalidMarker
          ? await documentsApi.saveLinks(invoiceId, ids, false, false, true)
          : await documentsApi.saveLinks(invoiceId, ids, flag);
      // What the SERVER did, not what we asked for — the guard can no-op on a
      // draft, and a toast that claims a flag it didn't set teaches distrust.
      const flagged = r?.data?.marked_invalid === true;
      track(EVENTS.DELIVERY_NOTES_LINKED, {
        linked_count: ids.length,
        matched_totals: matches,
        marked_invalid: flagged,
        selected_outside_filter: outsideRangeCount,
      });
      // Approving with nothing ticked removed nothing, so it has nothing to report.
      const nothingHappened = ids.length === 0 && comment !== undefined && existingLinkCount === 0 && !rows.some(n => n.already_linked_here);
      const what = ids.length === 0 ? 'הקישורים בוטלו' : ids.length === 1 ? 'קושרה תעודה אחת' : `קושרו ${ids.length} תעודות`;
      const confirmed = r?.data?.confirmed_by_amount === true;
      if (!nothingHappened) {
        toast(what + (flagged ? ' · החשבונית סומנה כלא תקין' : '')
                   + (confirmed ? ' · אושרה לפי סכום' : '')
                   + (r?.data?.restored_valid ? ' · החשבונית חזרה לתקין' : ''));
      }
      // After the links, never before: the month is approved only once the
      // save stood, and never on an invoice this same save flagged לא תקין.
      // The endpoint TOGGLES, so re-read the month first — one approved in the
      // meantime must not be revoked by a box that said "approve". A failure
      // here leaves the saved links alone and says so.
      // noMonth: a window that decided לא תקין saved the status before this save.
      if (approveMonth && monthApproval && !flagged && !noMonth) {
        const { supplierId, dateFrom, dateTo, monthLabel, hold = (work) => work(), land = () => {}, settle = async () => {} } = monthApproval;
        // A caller that may have a request out for this month gives us our turn,
        // and a caller holding a copy of the month gets the answer, inside it,
        // or the failed approval to settle, since it may have committed.
        await hold(async () => {
          try {
            const cur = await suppliersApi.getOne(supplierId, { date_from: dateFrom, date_to: dateTo });
            if (cur?.data?.lifecycle?.approved_by) {
              land(cur.data.lifecycle);
            } else {
              let a;
              try {
                a = await suppliersApi.approve(supplierId, dateFrom, dateTo);
              } catch (e) {
                await settle(e);
                throw e;
              }
              land(a?.data?.lifecycle);
              if (a?.data?.approved) toast(`${monthLabel} אושר לתשלום`);
            }
          } catch (e) {
            toast(e.response?.data?.error || 'הקישורים נשמרו, אך האישור לתשלום נכשל', 'error');
          }
        });
      } else if (approveMonth && monthApproval) {
        toast('החודש לא אושר לתשלום — החשבונית סומנה כלא תקין', 'error');
      }
      onSaved?.(ids);
      // Judged on the status the server reports after the save, not on the
      // prop: a flag that no-op'd leaves a draft the customer cannot see, and
      // a caller's status can lag behind a save that already moved it.
      const nowStatus = r?.data?.status ?? (flagged ? 'לא תקין' : status);
      // Confirming says nothing is missing — no list to send.
      const wantComment = !confirmByAmount && !confirmedNow && askableCount > 0
        && (comment ?? (sendComment && commentVisible));
      if (wantComment && nowStatus !== 'טיוטה') {
        setCommentOpen('afterSave');
      } else {
        // Asked for, but the invoice stayed a draft: say so rather than let
        // the admin believe the customer was told.
        if (wantComment) toast('רשימת החוסרים לא נשלחה — המסמך נשאר בטיוטה', 'error');
        onClose();
      }
    } catch (e) {
      toast(e.response?.data?.error || 'שגיאה בשמירת הקישורים', 'error');
      if (e.response?.status === 400) refreshList();
    } finally {
      setSaving(false);
    }
  };

  // ON A DRAFT, SAVING REMEMBERS THE TICKS AND NOTHING ELSE (owner, 02/11/2026):
  // no links, no month approval, no comment. A real link zeroes its note while a
  // draft invoice is not counted, so the balance would read low until approval
  // (server/lib/pendingLinks.js, and the server refuses those links too). The
  // only way to links from here is the flag, which takes it out of טיוטה.
  const canKeepDraft = isDraft && !loading && rows.length > 0 && !coverageFailed && !listFailed;
  const canFlagDraft = isDraft && !loading && hasFinding && canMarkInvalid && !coverageFailed && !listFailed;

  // The approve button's window: nothing is saved yet. Once a save stands the
  // status moves on, and the ordinary footer takes over if linking then fails.
  const deciding = !!decision && isDraft;
  const decisionReady = deciding && !loading && !coverageFailed && !listFailed;
  // Same notes under another numbering: the sums close, so there is no gap to flag
  // and nothing to tell the customer. Approving says so itself, as the button past
  // the draft does, and the server re-checks the sums.
  const sameNotesOtherNumbers = canConfirmByAmountOnApproval(askableCount, matches, invoiceNet, selected.size, confirmedNow);
  // Same test as the flag elsewhere: a net of 0 makes every selection a mismatch,
  // so it must not make לא תקין the default on a correct invoice.
  const flagIsDefault = hasFinding && canMarkInvalid && !sameNotesOtherNumbers;
  const decisionCommentOn = decisionComment ?? (!listSent && !sameNotesOtherNumbers);
  const decide = async (to, { byAmount = false, skipLinks = false } = {}) => {
    setPressed(to);
    setSaving(true);
    let written;
    try {
      written = await decision.save(to);
    } catch (e) {
      toast(e.response?.data?.error || 'שגיאה בשמירה', 'error');
    } finally {
      setSaving(false);
    }
    // Refused: the page said why, and the document is still a draft.
    if (!written) { setPressed(null); return; }
    setLiveStatus(written);
    // A credit line on the view-mode approve files it לא תקין whatever was asked.
    decidedRef.current = { invalidMarker: to === 'לא תקין' && flagIsDefault, noMonth: written !== 'תקין' };
    if (skipLinks) {
      onSaved?.([]);
      onClose();
      return;
    }
    await doSave(false, byAmount, { comment: decisionCommentOn });
    setPressed(null);
  };

  // Past the draft, a ticked note that is not linked yet counts in לתשלום BESIDE
  // the מרכזת (lib/amountOwed.js zeroes it only once linked), so the balance is
  // too high until this save. The window says so, and its buttons name the step.
  const unlinkedCount = selectedRows.filter(r => !r.already_linked_here).length;
  // The save is a full replacement, so an unticked link is a link removed.
  const existingLinkRows = rows.filter(r => r.already_linked_here);
  const linksRemoved = existingLinkRows.filter(r => !selected.has(r.id)).length;
  const leavesNotesUnlinked = !isDraft && !loading && unlinkedCount > 0;
  // Back to the button that opened it, or focus falls out of the dialog.
  const closeScan = () => { setScanOpen(false); scanOpenBtnRef.current?.focus(); };
  const closeWindow = () => {
    if (leavesNotesUnlinked) toast('התעודות לא קושרו · אפשר לקשר מעמוד המסמך', 'error');
    onClose();
  };
  // Escape is one stray key, so it never costs work. ביטול stays the one-click
  // exit. Over the scan it only leaves the scan. The Modal asks this handler only
  // when no layer sits above it (a date picker, the confirm).
  const onEscape = () => {
    // A save in flight closes the window itself when it lands.
    if (saving) return;
    if (scanOpen) { closeScan(); return; }
    const opened = openedSelectionRef.current;
    const changed = opened && (opened.size !== selected.size || [...selected].some(id => !opened.has(id)));
    if (changed) { toast('יש סימונים שלא נשמרו — יש לבחור ביטול או שמירה', 'error'); return; }
    closeWindow();
  };
  const keepAsDraft = async () => {
    setPressed('keep');
    setSaving(true);
    try {
      const ids = [...selected];
      // Nothing listed is no answer: a kept [] would read as "none of these" and
      // stop the window ticking the notes when they arrive.
      if (rows.length > 0) {
        // The notes this window listed, not whatever the server holds by now: a note
        // that arrived while it was open was never seen, so it may still be ticked.
        await documentsApi.savePendingLinks(invoiceId, ids, rows.map(r => r.id));
        const what = ids.length === 0 ? 'הסימונים נוקו' : ids.length === 1 ? 'נשמר סימון אחד' : `נשמרו ${ids.length} סימונים`;
        toast(`${what} · המסמך נשאר טיוטה`);
      }
      onSaved?.(ids);
      if (decision) await decision.keep();
      else onClose();
    } catch (e) {
      toast(e.response?.data?.error || 'שגיאה בשמירת הסימונים', 'error');
      if (e.response?.status === 400) refreshList();
    } finally {
      setSaving(false);
      setPressed(null);
    }
  };

  // The finding, not a summary: these are notes the supplier is billing for
  // that do not exist in the system. Rendered above the list because it is the
  // one thing on this screen the user cannot fix by ticking a box.
  //
  // A מרכזת that reconciles to the agora can still land here — a note attached
  // to a NEIGHBOURING month's invoice is filtered out of the candidate list by
  // the server, so it reads as missing. That is the intended reading: it is
  // the case this screen could never surface before.
  const awaitingLine = awaitingCount > 0 && (
    <div style={{
      fontSize: coverage.missing.length ? '0.8rem' : '0.9rem',
      fontWeight: coverage.missing.length ? 400 : 600,
      color: 'var(--warning-ink)',
      marginTop: coverage.missing.length ? 4 : 0,
    }}>
      {`${awaitingText} — יש לאשר ${awaitingCount === 1 ? 'אותה' : 'אותן'} כדי לקשר`}
    </div>
  );
  // "All found and ticked" has to be true of the ticks on screen: kept ticks can
  // leave out a note the invoice names, and the admin must not read that as done.
  const matchedUnticked = (coverage?.matched ?? []).filter(m => !selected.has(m.document_id)).length;
  const coveragePanel = coverage && (confirmedNow ? (
    <div className="link-coverage" style={{
      borderRadius: 8, padding: '10px 14px', marginBottom: 12,
      background: 'var(--success-bg)', border: '1px solid var(--success-line)',
    }}>
      <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--success-ink)' }}>
        <Icon name="check-circle" size={15} />{' '}
        אושרה לפי סכום — אותן תעודות במספור שונה
      </div>
      <div style={{ fontSize: '0.8rem', color: 'var(--success-ink)', marginTop: 4 }}>
        {coverage.missing.length} הפניות בחשבונית לא הותאמו לפי מספר
        {coverage.confirmed.by ? ` · אושר ע״י ${coverage.confirmed.by}` : ''}
        {coverage.confirmed.at ? ` · ${formatUtcDate(coverage.confirmed.at)}` : ''}
      </div>
      {awaitingLine}
    </div>
  ) : (
    <div className="link-coverage" style={{
      borderRadius: 8, padding: '10px 14px', marginBottom: 12,
      background: coverage.missing.length ? 'var(--danger-bg)' : (awaitingCount || matchedUnticked) ? 'var(--warning-bg)' : 'var(--success-bg)',
      border: `1px solid ${coverage.missing.length ? 'var(--danger-line)' : (awaitingCount || matchedUnticked) ? 'var(--warning-line)' : 'var(--success-line)'}`,
    }}>
      {coverage.missing.length === 0 && !awaitingCount && (
        matchedUnticked > 0 ? (
          <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--warning-ink)' }}>
            <Icon name="alert" size={15} />{' '}
            {matchedUnticked === 1 ? 'תעודה אחת מהחשבונית נמצאה ולא סומנה' : `${matchedUnticked} תעודות מהחשבונית נמצאו ולא סומנו`}
          </div>
        ) : (
          <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--success-ink)' }}>
            <Icon name="check-circle" size={15} />{' '}
            {coverage.covered_count === 1 ? 'התעודה שבחשבונית נמצאה וסומנה' : `כל ${coverage.covered_count} התעודות שבחשבונית נמצאו וסומנו`}
          </div>
        )
      )}
      {coverage.missing.length > 0 && (
        <>
          {/* One line: the references themselves are red rows in the list below,
              in the order they are read against the invoice. */}
          <div className="link-coverage-summary">
            <Icon name="alert" size={15} />{' '}
            {coverage.missing.length === 1 ? 'תעודה אחת מופיעה בחשבונית ולא נמצאה לקישור' : `${coverage.missing.length} תעודות מופיעות בחשבונית ולא נמצאו לקישור`}
            {coverage.missing_without_amount < coverage.missing.length && ` · ${formatCurrency(coverage.missing_total)}`}
          </div>
          {coverage.missing_without_amount > 0 && (
            <div style={{ fontSize: '0.85rem', color: 'var(--danger-ink)', marginTop: 4 }}>
              {missingTotalText(coverage)}
            </div>
          )}
          {status === 'טיוטה' && askableCount > 0 && (
            <div style={{ fontSize: '0.8rem', color: 'var(--ink-muted)', marginTop: 4 }}>
              הלקוח לא רואה טיוטה. אפשר לשלוח לו את הרשימה דרך ״סמן כלא תקין וקשר תעודות״
            </div>
          )}
        </>
      )}
      {awaitingLine}
      {coverage.missing.length > 0 && (
        <>
          {canConfirmByAmount && (
            <div className="link-confirm-amount">
              <button
                type="button"
                className="btn btn-sm btn-primary"
                disabled={saving}
                onClick={() => doSave(false, true)}
              >
                <Icon name="check" size={14} /> אשר – אותן תעודות במספור שונה
              </button>
              <span className="link-flag-hint">
                סכום התעודות שנבחרו תואם למרכזת (נטו). שומר את הקישור ומסמן את החשבונית כמלאה
              </span>
            </div>
          )}
          {/* A draft is invisible to the customer, so a comment on it would be
              emailed about a document they cannot open. From a draft the list
              goes out through the flag's confirm instead, once the flag has
              taken it out of טיוטה. */}
          {status !== 'טיוטה' && askableCount > 0 && (
            <button
              type="button"
              className="btn btn-sm btn-outline"
              style={{ marginTop: 8 }}
              onClick={() => setCommentOpen('panel')}
            >
              <Icon name="mail-send" size={14} /> שלח כהערה ללקוח
            </button>
          )}
        </>
      )}
    </div>
  ));

  // The period control. Rendered whenever there are
  // candidate rows at all — NOT inside the visible-rows branch: a range that
  // happens to hide everything must still leave the user a way back.
  const filterRow = (
    <div className="link-filter-row">
      <label className={`docs-filter-control docs-filter-searchpill link-note-search${search ? ' is-active' : ''}`}>
        <Icon name="search" size={14} className="docs-filter-microlabel" />
        <input
          className="docs-filter-select"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="חיפוש מספר תעודה"
          aria-label="חיפוש מספר תעודה"
        />
      </label>
      {isMobile && (
        <MobileSort options={LINK_SORT_OPTIONS} sort={listSort} onChange={setListSort} />
      )}
      <PeriodPicker
        dateFrom={range.from}
        dateTo={range.to}
        onChange={(from, to) => setRange({ from, to })}
        // The month-aligned set, not the /analysis rolling one: the question
        // this screen asks is "which calendar month does this מרכזת cover",
        // and 'חודש קודם' has to be a preset because it is the default range.
        presets={PRESETS_MONTHLY}
        variant={isMobile ? 'bar' : 'pill'}
      />
      {ticksAreGuess && !coverage && (
        <div className="link-guess-note">
          לא נקראו מהחשבונית מספרי תעודות, לכן סומנו התעודות של התקופה. יש לבדוק מול החשבונית.
          {guessLeftOut > 0 && ` ${guessLeftOut === 1 ? 'תעודה אחת ממוענת לנמען אחר ולא סומנה' : `${guessLeftOut} תעודות ממוענות לנמען אחר ולא סומנו`}.`}
        </div>
      )}
      {creditsInRange.length > 0 && (
        <div className="link-credit-hint">
          {creditsInRange.length === 1 ? `נמצאה חשבונית זיכוי פתוחה ${creditsWhere}` : `נמצאו ${creditsInRange.length} חשבוניות זיכוי פתוחות ${creditsWhere}`}
          {' · '}−{formatCurrency(Math.abs(creditsNet))}
          {creditsInRange.length === 1 ? ' · לא נספרת בסכומים ולא ניתן לקשר אותה' : ' · לא נספרות בסכומים ולא ניתן לקשר אותן'}
        </div>
      )}
    </div>
  );

  const selectAllStrip = (
    <label className="link-selectall">
      <input
        type="checkbox"
        checked={allVisibleSelected}
        ref={el => { if (el) el.indeterminate = someVisibleSelected; }}
        onChange={toggleAllVisible}
      />
      <span>בחר הכל בטווח ({visibleRows.length})</span>
    </label>
  );

  // rows.length > 0 but nothing survives the range. Distinct from "this
  // supplier has nothing to link", and it needs its own way out — the modal
  // opens pre-filtered, so this is a state the user can land in without ever
  // having touched the picker.
  const outOfRangeEmpty = (
    <div className="link-empty-range">
      <div>אין תעודות בטווח שנבחר — {rows.length + missingCount + creditPool.length} תעודות מחוץ לטווח</div>
      <button type="button" className="btn btn-sm btn-outline"
              onClick={() => setRange({ from: '', to: '' })}>
        הצג הכל
      </button>
    </div>
  );
  // A search that finds nothing is its own state: the notes are not out of range.
  // And it must not read as "not in the system" when the number is only out of
  // the range, which opens on one month.
  const searchOutOfRange = rows.some(r => matchesNoteSearch(r.doc_num, search))
    || openCredits.some(c => matchesNoteSearch(c.doc_num, search))
    || (!confirmedNow && (coverage?.missing ?? []).some(m => matchesNoteSearch(m.ref_num, search)));
  const noSearchMatch = (
    <div className="link-empty-range">
      <div>
        {searchOutOfRange
          ? `התעודה ״${search.trim()}״ מחוץ לטווח שנבחר`
          : `לא נמצאה תעודה עם המספר ״${search.trim()}״`}
      </div>
      {searchOutOfRange ? (
        <button type="button" className="btn btn-sm btn-outline" onClick={() => setRange({ from: '', to: '' })}>הצג הכל</button>
      ) : (
        <button type="button" className="btn btn-sm btn-outline" onClick={() => setSearch('')}>נקה חיפוש</button>
      )}
    </div>
  );
  const emptyList = search.trim() ? noSearchMatch : outOfRangeEmpty;

  // What to know about a note before trusting its row. One line of chips, shared by
  // the table and the phone list.
  const noteChips = (r) => (
    <>
      {isOffInvoice(r) && (
        <span className="link-not-on-invoice" title="התעודה קיימת במערכת אך לא מופיעה בחשבונית — לרוב היא שייכת לחודש אחר">
          לא במרכזת
        </span>
      )}
      {r.recipient_match === 'mismatch' && (
        <span className="link-not-on-invoice" title={r.extracted_recipient_name ? `התעודה ממוענת אל: ${r.extracted_recipient_name}` : 'התעודה ממוענת לעסק אחר'}>
          נמען אחר
        </span>
      )}
      {r.price_note && (
        <span className="link-not-on-invoice" title="חלק מהמחירים לא הופיעו על התעודה, לכן הסכום שלה אינו סופי">
          {r.price_note === 'unpriced' ? 'חסרים מחירים' : 'מחירים משוערים'}
        </span>
      )}
    </>
  );
  // The red row's number: a link when the reference exists in another form.
  const missingNumber = (r) => (r.source.blocker
    ? <a className="link-note-open" href={noteHref(r.source.blocker.document_id)} target="_blank" rel="noopener noreferrer" title={NOTE_LINK_TITLE}
         onClick={e => e.stopPropagation()}>{r.doc_num} <Icon name="external" size={13} /></a>
    : r.doc_num);
  const missingChip = (r) => <span className="link-missing-chip">{blockerLabel(r.source.blocker) || 'חסרה'}</span>;

  const linking = (
    <>
      <h2 className="modal-title" style={{ marginBottom: 6 }}>
        קישור תעודות משלוח / החזרה
        {deciding && <span className="badge badge-yellow link-title-status">טיוטה</span>}
      </h2>
      <div style={{ color: 'var(--ink-muted)', fontSize: '0.9rem', marginBottom: 12 }}>
        {/* Gross is context here. The net, the target, sits in the sticky totals. */}
        חשבונית מרכזת — {supplierName}
        {invoiceNet > 0
          ? ` · כולל מע״מ: ${formatCurrency(invTotal)}`
          : ` · סכום החשבונית: ${formatCurrency(invTotal)} (כולל מע״מ)`}
        {hasScan && !split && (
          <button type="button" ref={scanOpenBtnRef} className="btn btn-sm btn-outline link-scan-open" onClick={() => setScanOpen(true)}>
            <Icon name="documents" size={14} /> הצג מסמך
          </button>
        )}
      </div>

      {!loading && coveragePanel}

      {loading ? (
        <div className="loading-container"><div className="spinner" /><span>טוען...</span></div>
      ) : listFailed ? (
        <EmptyState message="רשימת התעודות לא נטענה" />
      ) : rows.length === 0 && openCredits.length === 0 && !(coverage?.missing?.length && !confirmedNow) ? (
        <EmptyState message={awaitingCount > 0
          ? 'אין עדיין תעודות מאושרות פנויות לקישור עבור ספק זה'
          : 'אין תעודות זמינות לקישור עבור ספק זה'} />
      ) : isMobile ? (
        <>
          {filterRow}
          {/* No select-all above an empty range: a disabled control stacked
              on a panel that already explains the state is just noise. */}
          {visibleRows.length > 0 && selectAllStrip}
          {listRows.length === 0 ? emptyList : (
            <div className="m-list link-notes-list">
              {listRows.map(r => {
                if (r.isMissing) {
                  return (
                    <div className="link-note-row is-missing" key={r.id}>
                      <div className="m-row m-row--static">
                        <span className="m-row-main">
                          <span className="m-row-primary">
                            {r.doc_num}{missingChip(r)}
                          </span>
                          <span className="m-row-meta">
                            {formatDateShort(r.date) || '—'}
                            <span>· {missingKindLabel(r.source)}</span>
                          </span>
                        </span>
                        <span className="m-row-end">
                          <span className="m-row-amount">{missingAmountText(r.source)}</span>
                        </span>
                      </div>
                      {r.source.blocker && (
                        <a className="link-note-open-tap" href={noteHref(r.source.blocker.document_id)} target="_blank" rel="noopener noreferrer"
                           title={NOTE_LINK_TITLE} aria-label={`${NOTE_LINK_TITLE}: ${r.doc_num}`}>
                          <Icon name="external" size={16} />
                        </a>
                      )}
                    </div>
                  );
                }
                if (r.isCredit) {
                  return (
                    <div className="link-note-row is-credit" key={r.id}>
                      <div className="m-row m-row--static">
                        <input type="checkbox" disabled aria-label={`חשבונית זיכוי ${r.doc_num || ''}: לא ניתן לקשר כרגע`}
                               style={{ width: 17, height: 17, flex: 'none' }} />
                        <span className="m-row-main">
                          <span className="m-row-primary">{r.doc_num || '—'}<span className="link-credit-chip">לא נספרת</span></span>
                          <span className="m-row-meta">
                            {formatDateShort(r.date) || '—'}
                            <span>· {r.doc_type}</span>
                          </span>
                        </span>
                        <span className="m-row-end">
                          <span className="m-row-amount">−{formatCurrency(Math.abs(r.net))}</span>
                        </span>
                      </div>
                      <a className="link-note-open-tap" href={noteHref(r.documentId)} target="_blank" rel="noopener noreferrer"
                         title={NOTE_LINK_TITLE} aria-label={`${NOTE_LINK_TITLE}: ${r.doc_num || r.documentId}`}>
                        <Icon name="external" size={16} />
                      </a>
                    </div>
                  );
                }
                const checked = selected.has(r.id);
                const isReturn = r.doc_type === 'תעודת החזרה';
                return (
                  <div className="link-note-row" key={r.id}>
                    <button type="button" onClick={() => toggle(r.id)}
                            className={`m-row ${checked ? 'is-selected' : ''}`} aria-pressed={checked}>
                      <input type="checkbox" checked={checked} readOnly tabIndex={-1}
                             style={{ pointerEvents: 'none', width: 17, height: 17, flex: 'none' }} />
                      <span className="m-row-main">
                        <span className="m-row-primary">
                          {r.doc_num || '—'}
                          {noteChips(r)}
                        </span>
                        <span className="m-row-meta">
                          {formatDateShort(r.date) || '—'}
                          <span>· {r.doc_type || '—'}</span>
                          {r.recipient_match === 'mismatch' && r.extracted_recipient_name && <span>· {r.extracted_recipient_name}</span>}
                        </span>
                      </span>
                      <span className="m-row-end">
                        <span className="m-row-amount">
                          {isReturn ? '−' : ''}{formatCurrency(Math.abs(signedNet(r)))}
                        </span>
                      </span>
                    </button>
                    {/* Beside the row, not in it: the row is a <button>. */}
                    <a className="link-note-open-tap" href={noteHref(r.id)} target="_blank" rel="noopener noreferrer"
                       title={NOTE_LINK_TITLE} aria-label={`${NOTE_LINK_TITLE}: ${r.doc_num || r.id}`}>
                      <Icon name="external" size={16} />
                    </a>
                  </div>
                );
              })}
            </div>
          )}
        </>
      ) : (
        <>
          {filterRow}
          {listRows.length === 0 ? emptyList : (
            <div className="table-wrapper" style={{ marginBottom: 12 }}>
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>
                      <input
                        type="checkbox"
                        checked={allVisibleSelected}
                        disabled={visibleRows.length === 0}
                        ref={el => { if (el) el.indeterminate = someVisibleSelected; }}
                        onChange={toggleAllVisible}
                        title="בחר הכל בטווח"
                        aria-label="בחר הכל בטווח"
                      />
                    </th>
                    {/* Number first: it is what the eye matches on the invoice. */}
                    <th {...sortableTh('number')}>מספר תעודה{sortIcon('number')}</th>
                    <th {...sortableTh('date')}>תאריך{sortIcon('date')}</th>
                    <th>סוג</th>
                    <th>סכום נטו</th>
                  </tr>
                </thead>
                <tbody>
                  {listRows.map(r => {
                    if (r.isMissing) {
                      return (
                        <tr key={r.id} className="link-missing-row" title={r.source.blocker ? 'מופיעה בחשבונית, אך אי אפשר לקשר אותה כך' : 'מופיעה בחשבונית ולא נמצאה לקישור'}>
                          <td />
                          <td>{missingNumber(r)}{missingChip(r)}</td>
                          <td>{formatDateShort(r.date) || '—'}</td>
                          <td style={{ fontSize: '0.85rem' }}>{missingKindLabel(r.source)}</td>
                          <td style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{missingAmountText(r.source)}</td>
                        </tr>
                      );
                    }
                    if (r.isCredit) {
                      return (
                        <tr key={r.id} className="link-credit-row" title="חשבונית זיכוי פתוחה: לא נספרת בסכומים ולא ניתן לקשר אותה כרגע">
                          <td><input type="checkbox" disabled aria-label={`חשבונית זיכוי ${r.doc_num || ''}: לא ניתן לקשר כרגע`} /></td>
                          <td>
                            <a className="link-note-open" href={noteHref(r.documentId)} target="_blank" rel="noopener noreferrer" title={NOTE_LINK_TITLE}>
                              {r.doc_num || '—'} <Icon name="external" size={13} />
                            </a>
                            <span className="link-credit-chip">לא נספרת</span>
                          </td>
                          <td>{formatDateShort(r.date) || '—'}</td>
                          <td style={{ fontSize: '0.85rem' }}>{r.doc_type}</td>
                          <td style={{ fontVariantNumeric: 'tabular-nums' }}>−{formatCurrency(Math.abs(r.net))}</td>
                        </tr>
                      );
                    }
                    const checked = selected.has(r.id);
                    const isReturn = r.doc_type === 'תעודת החזרה';
                    return (
                      <tr key={r.id} onClick={() => toggle(r.id)}
                          style={{ cursor: 'pointer', background: checked ? 'var(--selected-soft-bg)' : undefined }}>
                        <td><input type="checkbox" aria-label={`בחר תעודה ${r.doc_num || r.id}`} checked={checked} onChange={() => toggle(r.id)} onClick={e => e.stopPropagation()} /></td>
                        <td style={{ color: 'var(--ink-muted)' }}>
                          <a className="link-note-open" href={noteHref(r.id)} target="_blank" rel="noopener noreferrer"
                             title={NOTE_LINK_TITLE} onClick={e => e.stopPropagation()}>
                            {r.doc_num || '—'} <Icon name="external" size={13} />
                          </a>
                          {noteChips(r)}
                        </td>
                        <td>{formatDateShort(r.date) || '—'}</td>
                        <td style={{ color: 'var(--ink-muted)', fontSize: '0.85rem' }}>{r.doc_type || '—'}</td>
                        {/* Neutral ink, minus sign kept: red in this list now means missing. */}
                        <td style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                          {isReturn ? '−' : ''}{formatCurrency(Math.abs(signedNet(r)))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Sticky: with dozens of notes the running sum, the gap and the save
          button are exactly what must NOT scroll away — the whole modal exists
          to watch that sum approach the invoice's נטו. */}
      <div className="link-modal-foot">
        {!loading && (
          <div className="link-totals">
            <div className="link-total">
              <span className="link-total-label">
                {selected.size === 1 ? 'נבחרה תעודה אחת' : `נבחרו ${selected.size} תעודות`}
                {outsideRangeCount > 0 && <span> · {outsideRangeCount} מחוץ לטווח</span>}
              </span>
              <strong className="link-total-value">{formatCurrency(linkedNet)}</strong>
            </div>
            {totals.missingCount > 0 && (
              <div className="link-total is-missing" title="הסכומים כפי שמודפסים בחשבונית">
                <span className="link-total-label">
                  חסרות {totals.missingCount}
                  {totals.unpricedCount > 0 && totals.withMissing != null && ` · ל-${totals.unpricedCount} אין סכום`}
                </span>
                <strong className="link-total-value">
                  {totals.withMissing == null ? 'ללא סכום' : formatCurrency(totals.missingNet)}
                </strong>
              </div>
            )}
            {totals.missingCount > 0 && totals.withMissing != null && gap?.kind !== 'numbering' && (
              <div className="link-total">
                <span className="link-total-label">סה״כ כולל חסרות</span>
                <strong className="link-total-value">{formatCurrency(totals.withMissing)}</strong>
              </div>
            )}
            {invoiceNet > 0 && (
              <div className="link-total">
                <span className="link-total-label">מרכזת (נטו)</span>
                <strong className="link-total-value">{formatCurrency(invoiceNet)}</strong>
              </div>
            )}
            {(gap?.kind === 'short' || gap?.kind === 'over') && (
              <div className="link-total is-gap">
                <span className="link-total-label">{gap.kind === 'short' ? 'חסר מול המרכזת' : 'עודף מול המרכזת'}</span>
                <strong className="link-total-value">{formatCurrency(gap.amount)}</strong>
              </div>
            )}
            {(gap?.kind === 'match' || gap?.kind === 'explained') && (
              // Not a green tick when a blocker is part of it: a note billed on two
              // invoices closes the sum and is still a problem.
              <div className={`link-total-note${coverage?.missing.some(m => m.blocker) ? '' : ' is-ok'}`}>
                {!coverage?.missing.some(m => m.blocker) && <Icon name="check-circle" size={15} />} {gapLabel(gap)}
              </div>
            )}
            {gap?.kind === 'numbering' && (
              <div className="link-total-note">
                הסכום תואם למרכזת, אבל {totals.missingCount === 1 ? 'הפניה אחת לא נמצאה' : `${totals.missingCount} הפניות לא נמצאו`}.
                {askableCount > 0 && ' ייתכן שזה מספור שונה אצל הספק.'}
                {askableCount > 0 && isDraft && !deciding && ' אפשר לאשר לפי סכום אחרי אישור הטיוטה.'}
              </div>
            )}
          </div>
        )}
        {!deciding && canKeepDraft && (
          <div className="link-draft-note">
            בטיוטה נשמרים הסימונים בלבד. הקישור בפועל נעשה אחרי אישור המסמך.
            {existingLinkRows.length > 0 && ` ${existingLinkRows.length === 1 ? 'קישור קיים נשאר' : `${existingLinkRows.length} קישורים קיימים נשארים`} עד אז.`}
          </div>
        )}
        {!loading && (coverageFailed || listFailed) && (
          <div className="link-unlinked-warning link-load-failed" role="alert">
            <span>
              <Icon name="alert" size={15} />{' '}
              {listFailed ? 'לא ניתן לטעון את התעודות.' : 'לא ניתן לטעון את הסימונים שנשמרו, ולכן אי אפשר לשמור כרגע.'}
            </span>
            <button type="button" className="btn btn-sm btn-outline" onClick={() => setLoadAttempt(n => n + 1)}>נסה שוב</button>
          </div>
        )}
        {!loading && !isDraft && linksRemoved > 0 && (
          <div className="link-draft-note">
            {linksRemoved === 1 ? 'קישור קיים יוסר בשמירה' : `${linksRemoved} קישורים קיימים יוסרו בשמירה`}
          </div>
        )}
        {leavesNotesUnlinked && (
          <div className="link-unlinked-warning" role="status">
            <Icon name="alert" size={15} />{' '}
            {unlinkedCount === 1
              ? 'תעודה אחת שסומנה עדיין לא מקושרת. עד שתקושר, הסכום שלה נספר לתשלום בנוסף למרכזת.'
              : `${unlinkedCount} תעודות שסומנו עדיין לא מקושרות. עד שיקושרו, הסכום שלהן נספר לתשלום בנוסף למרכזת.`}
          </div>
        )}
        {/* On a draft only where the approve button opened it: anywhere else nothing
            is being decided, and approving offers it then. */}
        {monthApproval && (!isDraft || decisionReady) && (
          <label className="link-approve-month">
            <input type="checkbox" checked={approveMonth}
                   onChange={e => setApproveMonth(e.target.checked)} />
            {deciding ? (
              <span>אשר גם לתשלום ({monthApproval.monthLabel}) · רק בלחיצה על אשר</span>
            ) : (
              <span>
                אשר גם לתשלום ({monthApproval.monthLabel})
                <span className="link-flag-hint">מאשר את כל החודש של הספק יחד עם שמירת הקישורים</span>
              </span>
            )}
          </label>
        )}
        {decisionReady && askableCount > 0 && !confirmedNow && (
          <label className="link-approve-month">
            <input type="checkbox" checked={decisionCommentOn}
                   onChange={e => setDecisionComment(e.target.checked)} />
            <span>
              פתח גם הערה ללקוח עם רשימת החוסרים
              <span className="link-flag-hint">חלון ההערות ייפתח אחרי השמירה. לא חל על שמירה בטיוטה</span>
            </span>
          </label>
        )}
        {deciding ? (
          // Two groups, so a narrow column wraps the actions above the exits and
          // never leaves the default alone on a row of its own.
          <div className="modal-actions link-decide-row">
            <div className="link-decide-exits">
              <DecisionButton className="btn btn-secondary" tipId="link-tip-cancel"
                              tip={decision.savedFirst ? 'חזרה למסמך. השינויים כבר נשמרו כטיוטה' : 'חזרה למסמך בלי לשמור'}
                              onClick={closeWindow} disabled={saving}>ביטול</DecisionButton>
              {decisionReady && (
                <DecisionButton className="btn btn-secondary" tipId="link-tip-keep"
                                tip={decision.movesOn ? 'נשאר טיוטה. העריכות והסימונים נשמרים, ועוברים לטיוטה הבאה' : 'נשאר טיוטה. הסימונים נשמרים'}
                                onClick={keepAsDraft} disabled={saving}>{pressed === 'keep' ? 'שומר…' : 'שמור בטיוטה'}</DecisionButton>
              )}
            </div>
            <div className="link-decide-actions">
              {decisionReady ? (
                <>
                  <DecisionButton className={`btn ${flagIsDefault ? 'btn-outline' : 'btn-primary'} link-decide-approve`}
                                  tipId="link-tip-approve"
                                  tip={(sameNotesOtherNumbers ? 'המסמך יאושר. הסכום תואם, והתעודות במספור שונה אצל הספק'
                                    : selected.size > 0 ? 'המסמך יאושר כתקין והתעודות שסומנו יקושרו אליו'
                                    : 'המסמך יאושר כתקין בלי תעודות מקושרות')
                                    + (approveMonth && monthApproval ? ', והחודש יאושר לתשלום' : '')}
                                  onClick={() => decide('תקין', { byAmount: sameNotesOtherNumbers })} disabled={saving}>
                    {pressed === 'תקין' ? 'שומר…'
                      : sameNotesOtherNumbers ? 'אשר – אותן תעודות במספור שונה'
                      : selected.size > 0 ? `אשר וקשר ${countNotes(selected.size)}${flagIsDefault ? ' בכל זאת' : ''}`
                      : flagIsDefault ? 'אשר בכל זאת, בלי לקשר' : 'אשר בלי לקשר'}
                  </DecisionButton>
                  <DecisionButton className={`btn ${flagIsDefault ? 'btn-caution' : 'btn-outline'} link-decide-flag`}
                                  tipId="link-tip-flag"
                                  tip={selected.size > 0 ? 'המסמך יישמר כלא תקין והתעודות שסומנו יקושרו אליו' : 'המסמך יישמר כלא תקין בלי תעודות מקושרות'}
                                  onClick={() => decide('לא תקין')} disabled={saving}>
                    {pressed === 'לא תקין' ? 'שומר…' : selected.size > 0 ? `סמן כלא תקין וקשר ${countNotes(selected.size)}` : 'סמן כלא תקין'}
                  </DecisionButton>
                </>
              ) : !loading && (
                // A failed read means the ticks are a guess, so none are saved. It must
                // not stop the approval, though: the link stays an offer, not a gate.
                <DecisionButton className="btn btn-outline link-decide-approve" tipId="link-tip-approve"
                                tip="המסמך יאושר כתקין. אפשר לקשר אחר כך מעמוד המסמך"
                                onClick={() => decide('תקין', { skipLinks: true })} disabled={saving}>{pressed === 'תקין' ? 'שומר…' : 'אשר בלי לקשר'}</DecisionButton>
              )}
            </div>
          </div>
        ) : (
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={closeWindow} disabled={saving}>
            {isDraft ? 'ביטול' : leavesNotesUnlinked ? 'סגור בלי לקשר' : 'סגור'}
          </button>
          {isDraft ? (
            <>
              {canFlagDraft && (
                <button className="btn btn-outline" onClick={handleSave} disabled={saving}>
                  {selected.size > 0 ? 'סמן כלא תקין וקשר תעודות…' : 'סמן כלא תקין…'}
                </button>
              )}
              {canKeepDraft && (
                <button className="btn btn-primary" onClick={keepAsDraft} disabled={saving}>
                  {saving ? 'שומר…' : 'שמור סימונים בטיוטה'}
                </button>
              )}
            </>
          ) : (
            <button className="btn btn-primary" onClick={handleSave} disabled={loading || saving || listFailed || coverageFailed}>
              {saving ? 'שומר…' : unlinkedCount > 0 ? `קשר ${countNotes(unlinkedCount)}` : 'שמור קישורים'}
            </button>
          )}
        </div>
        )}
      </div>
    </>
  );

  return (
    <Modal
      onClose={onEscape}
      className={`link-modal${split ? ' is-split' : ''}`}
      size={split ? 1480 : 'lg'}
      closeOnBackdrop={false}
      ariaLabel="קישור תעודות משלוח / החזרה"
    >
      {split ? (
        <div className="link-split">
          <div className="link-split-main">{linking}</div>
          <ReviewDocPane files={files} onRotate={onRotate}
                         widthKey="link-doc-pane-width" defaultWidth={50} placement="left" />
        </div>
      ) : linking}

      {scanOpen && !split && (
        <div className="link-scan-sheet" role="dialog" aria-label="המסמך המקורי">
          <div className="link-scan-sheet-bar">
            {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
            <button type="button" autoFocus className="btn btn-secondary" onClick={closeScan}>חזרה לרשימה</button>
          </div>
          <ReviewDocPane files={files} onRotate={onRotate} widthKey="link-doc-pane-width" />
        </div>
      )}

      {commentOpen && (
        <CommentsModal
          type="document"
          referenceId={invoiceId}
          title={`מסמך ${invoiceDocNum || invoiceId}`}
          initialText={missingNotesComment(coverage)}
          onClose={closeComment}
          // Here the thread is a step in a flow, not a conversation: once the
          // list is sent, move on (in the upload flow, to the next document)
          // instead of leaving the thread open and looking stuck.
          onCommentAdded={() => { toast('ההערה נשלחה ללקוח'); closeComment(); }}
        />
      )}

      {/* Inside the stopPropagation box — see ClassificationManager for why an
          un-stopped click on ConfirmModal's own backdrop otherwise bubbles
          through to this modal's onClose and closes both at once. */}
      {confirmState && (
        <ConfirmModal
          {...confirmState}
          // After the spread, so the live checkbox value wins over anything
          // captured when the dialog was opened.
          onConfirm={() => doSave(isDraft ? true : markInvalid)}
          onCancel={() => setConfirmState(null)}
        >
          {canMarkInvalid && !isDraft && (
            <label className="link-flag-invalid">
              <input
                type="checkbox"
                checked={markInvalid}
                onChange={e => setMarkInvalid(e.target.checked)}
              />
              <span>
                {missingCount > 0
                  ? `סמן את החשבונית כלא תקין (${missingCount === 1 ? 'תעודה אחת חסרה' : `${missingCount} תעודות חסרות`})`
                  : `סמן את החשבונית כלא תקין (הפרש ${formatCurrency(Math.abs(diff))})`}
                <span className="link-flag-hint">כדי שההפרש לא ייעלם — אפשר לבטל את הסימון ידנית לאחר שיטופל</span>
              </span>
            </label>
          )}
          {askableCount > 0 && (
            <label className="link-flag-invalid" style={{ marginTop: 10, opacity: commentVisible ? 1 : 0.55 }}>
              <input
                type="checkbox"
                checked={commentVisible && sendComment}
                disabled={!commentVisible}
                onChange={e => setSendComment(e.target.checked)}
              />
              <span>
                פתח הערה ללקוח עם רשימת החוסרים
                <span className="link-flag-hint">חלון ההערות ייפתח אחרי השמירה עם הרשימה — שם בוחרים למי לשלוח במייל</span>
              </span>
            </label>
          )}
        </ConfirmModal>
      )}
    </Modal>
  );
}
